import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CreditCard from "../../components/CreditCard";
import DashboardAccounts from "../../components/DashboardAccounts";
import Sidebar from "../../components/Sidebar";
import StatusBanner from "../../components/StatusBanner";
import CurrencyToggleBar from "../../components/CurrencyToggleBar";
import ProfileCompletionWizard from "../../components/ProfileCompletionWizard";
import AgreementDetailsModal from "../../components/AgreementDetailsModal";
import { forceSessionLogout, getHomePageInfos, signForcedAgreement } from "../../utils/apiClient";
import SignatureCanvas from "react-signature-canvas";
import PendingTransactionsController from "../../controller/Transaction/pendingTransactionsController";
import TransactionHistoryController from "../../controller/Transaction/transactionHistoryController";
import AppConstants from "../../utils/appConstants";
import WalletController from "../../controller/walletController";
import { toApiDateParam } from "../../utils/dateRangeApiFormat";
import { TransactionType, TransactionStatus } from "../../utils/enums";
import { useToast } from "../../contexts/ToastContext";
import "../css/Dashboard.css";

const toNumberSafe = (value, fallback = 0) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const formatTickValue = (value) => {
  const numericValue = toNumberSafe(value, 0);
  if (numericValue >= 1000) return `${Math.round(numericValue / 1000)}K`;
  return `${Math.round(numericValue)}`;
};

const buildYAxisTicks = (maxValue) => {
  const safeMax = Math.max(toNumberSafe(maxValue, 0), 1);
  const roundedStep = Math.max(Math.ceil(safeMax / 5), 1);
  const topValue = roundedStep * 5;
  return [topValue, topValue * 0.8, topValue * 0.6, topValue * 0.4, topValue * 0.2, 0];
};

const formatTransactionDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit" });
};

const transactionTypeToLabel = (typeValue, t) => {
  const numericType = Number(typeValue);
  if (!Number.isFinite(numericType)) {
    return t("transaction", { ns: "mobile", defaultValue: "Transaction" });
  }

  const typeMap = {
    [TransactionType.INTERNAL_TRANSFER]: t("internal_transfer", { ns: "mobile", defaultValue: "Internal Transfer" }),
    [TransactionType.ADMIN_FEES]: t("admin_fees", { ns: "mobile", defaultValue: "Admin Fees" }),
    [TransactionType.GENERATE]: t("generate", { ns: "mobile", defaultValue: "Generate" }),
    [TransactionType.DEPOSIT]: t("deposit", { ns: "mobile", defaultValue: "Deposit" }),
    [TransactionType.WITHDRAWAL]: t("withdrawal", { ns: "mobile", defaultValue: "Withdrawal" }),
    [TransactionType.CASHIN]: t("cashin", { ns: "mobile", defaultValue: "Cash In" }),
    [TransactionType.CASHOUT]: t("cashout", { ns: "mobile", defaultValue: "Cash Out" }),
    [TransactionType.COMMISSION]: t("commission", { ns: "mobile", defaultValue: "Commission" }),
    [TransactionType.CREDIT_IN]: t("credit_in", { ns: "mobile", defaultValue: "Credit In" }),
    [TransactionType.CREDIT_OUT]: t("credit_out", { ns: "mobile", defaultValue: "Credit Out" }),
    [TransactionType.EXCHANGE]: t("exchange", { ns: "mobile", defaultValue: "Exchange" }),
    [TransactionType.REFUND]: t("refund", { ns: "mobile", defaultValue: "Refund" }),
    [TransactionType.PAY_BY_CARD]: t("pay_with_card", { ns: "mobile", defaultValue: "Pay By Card" }),
    [TransactionType.RESET_WALLET]: t("reset_wallet", { ns: "mobile", defaultValue: "Reset Wallet" }),
    [TransactionType.RESET_BALANCE_MT5]: t("reset_mt5_balance", { ns: "mobile", defaultValue: "Reset MT5 Balance" }),
    [TransactionType.COLLECTED]: t("collected", { ns: "mobile", defaultValue: "Collected" }),
    [TransactionType.REFUND_COLLECTED]: t("refund_collect", { ns: "mobile", defaultValue: "Refund Collected" }),
    [TransactionType.TRANSFER]: t("transfer", { ns: "mobile", defaultValue: "Transfer" }),
    [TransactionType.CASHIN_MT5]: t("cashin", { ns: "mobile", defaultValue: "Cash In" }),
    [TransactionType.BALANCE_REPORT]: t("balance", { ns: "mobile", defaultValue: "Balance Report" }),
  };

  return typeMap[numericType] || t("transaction", { ns: "mobile", defaultValue: "Transaction" });
};

const mapTransactionWordLabel = (value, t) => {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const normalized = raw.toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ");
  if (normalized === "cash in" || normalized === "cashin") return t("cashin", { ns: "mobile" });
  if (normalized === "cash out" || normalized === "cashout") return t("cashout", { ns: "mobile" });
  if (normalized === "deposit") return t("deposit", { ns: "mobile" });
  if (normalized === "withdraw" || normalized === "withdrawal") return t("withdrawal", { ns: "mobile" });
  if (normalized === "internal transfer" || normalized === "internaltransfer") {
    return t("internal_transfer", { ns: "mobile" });
  }
  return null;
};

const getDashboardTransactionTypeClass = (typeValue, typeLabel) => {
  const numericType = Number(typeValue);
  if (Number.isFinite(numericType)) {
    if (numericType === TransactionType.CASHIN || numericType === TransactionType.CASHIN_MT5) {
      return "type-cashin";
    }
    if (numericType === TransactionType.CASHOUT) {
      return "type-cashout";
    }
    if (
      numericType === TransactionType.INTERNAL_TRANSFER ||
      numericType === TransactionType.TRANSFER
    ) {
      return "type-transfer";
    }
  }

  const labelText = String(typeLabel || "").toLowerCase();
  if (labelText.includes("cash in") || labelText.includes("deposit")) return "type-cashin";
  if (labelText.includes("cash out") || labelText.includes("withdraw")) return "type-cashout";
  if (labelText.includes("internal transfer") || labelText.includes("transfer")) {
    return "type-transfer";
  }
  return "type-real";
};

const buildAvatarText = (title) => {
  const text = String(title || "").trim();
  if (!text) return "T";
  const parts = text.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
  return (parts[0].slice(0, 2) || "T").toUpperCase();
};

const formatMoney = (value) => {
  return `$${toNumberSafe(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const getStatusBannerVisibility = (accountUser) => {
  if (!accountUser) return false;

  const profileCompleted = accountUser.PROFILE_STATUS === 1;
  if (!profileCompleted) return true;

  const isEmailNotVerified =
    accountUser.user_information?.IS_EMAIL_VERIFIED === 0 ||
    accountUser.userInformation?.IS_EMAIL_VERIFIED === 0;
  if (isEmailNotVerified) return true;

  const requiredToResetPin =
    accountUser.REQUIRED_TO_RESET_PIN === 1 ||
    accountUser.requiredToResetPin === true;
  if (requiredToResetPin) return true;

  const kycStatus =
    accountUser.user_information?.IS_KYC_VERIFIED ??
    accountUser.userInformation?.IS_KYC_VERIFIED ??
    accountUser.user_information?.isKycVerified;

  if (kycStatus === 0 || kycStatus === -1) return true;
  if (kycStatus === 1 || kycStatus === -3 || kycStatus === -2) return true;
  return false;
};

const hasDashboardStatistics = (stats) => {
  if (!stats) return false;

  const hasFlowData =
    Array.isArray(stats.transactionFlow) &&
    stats.transactionFlow.some(
      (item) => toNumberSafe(item?.income, 0) > 0 || toNumberSafe(item?.outcome, 0) > 0
    );
  const hasTotals = toNumberSafe(stats.income, 0) > 0 || toNumberSafe(stats.expense, 0) > 0;
  const hasRecentTransactions =
    Array.isArray(stats.recentTransactions) && stats.recentTransactions.length > 0;
  const hasPendingTransactions =
    Array.isArray(stats.pendingTransactions) && stats.pendingTransactions.length > 0;

  return hasFlowData || hasTotals || hasRecentTransactions || hasPendingTransactions;
};

const isPendingTransactionStatus = (statusValue) => {
  const numericStatus = Number(statusValue);
  if (Number.isFinite(numericStatus)) {
    return numericStatus === 0 || numericStatus === 1 || numericStatus === 6 || numericStatus === 7;
  }

  const statusText = String(statusValue || "").toLowerCase();
  return statusText.includes("pending") || statusText.includes("waiting") || statusText.includes("progress");
};

const mapDashboardTransactionItem = (item, index, prefix = "recent", t) => {
  const typeCodeRaw = item?.TYPE ?? item?.type;
  const typeCodeNum = Number(typeCodeRaw);
  const labelFromType = transactionTypeToLabel(typeCodeRaw, t);
  const rawTitle = item?.TITLE || item?.title || item?.NAME || item?.name;
  const translatedTitle = mapTransactionWordLabel(rawTitle, t);
  const title =
    typeCodeNum === TransactionType.INTERNAL_TRANSFER
      ? labelFromType
      : translatedTitle ||
        rawTitle ||
        labelFromType;

  const statusCode =
    item?.STATUS ??
    item?.status ??
    item?.TRANSACTION_STATUS ??
    item?.transactionStatus ??
    item?.STATE ??
    item?.state;
  const statusLabel =
    item?.STATUS_LABEL ||
    item?.STATUS_NAME ||
    item?.statusLabel ||
    item?.statusName ||
    item?.STATUS_TEXT ||
    item?.statusText;

  const translatedTypeLabelText = mapTransactionWordLabel(
    item?.CATEGORY || item?.category,
    t
  );

  const typeLabelText =
    translatedTypeLabelText ||
    labelFromType;

  return {
    id: item?.TRANSACTION_ID || item?.ID || item?.id || `${prefix}-${index}`,
    title,
    type: typeLabelText,
    typeClass: getDashboardTransactionTypeClass(typeCodeRaw, typeLabelText),
    amount: toNumberSafe(item?.AMOUNT ?? item?.amount ?? item?.VALUE ?? item?.value, 0),
    date: formatTransactionDate(
      item?.DATE || item?.date || item?.TRANSACTION_DATE || item?.transactionDate
    ),
    avatar:
      item?.AVATAR ||
      item?.avatar ||
      item?.ICON_TEXT ||
      item?.iconText ||
      buildAvatarText(title),
    isPending:
      isPendingTransactionStatus(statusCode) || isPendingTransactionStatus(statusLabel),
  };
};

const extractTransactionsList = (rawValue) => {
  if (Array.isArray(rawValue)) return rawValue;
  if (!rawValue || typeof rawValue !== "object") return [];

  if (Array.isArray(rawValue.data)) return rawValue.data;
  if (Array.isArray(rawValue.DATA)) return rawValue.DATA;
  if (Array.isArray(rawValue.items)) return rawValue.items;
  if (Array.isArray(rawValue.transactions)) return rawValue.transactions;
  if (Array.isArray(rawValue.list)) return rawValue.list;

  if (rawValue.DATA && typeof rawValue.DATA === "object") {
    if (Array.isArray(rawValue.DATA.data)) return rawValue.DATA.data;
    if (Array.isArray(rawValue.DATA.items)) return rawValue.DATA.items;
    if (Array.isArray(rawValue.DATA.transactions)) return rawValue.DATA.transactions;
  }

  return [];
};

const mapPendingResultToDashboardItems = (pendingResult, t) => {
  const fromItems = Array.isArray(pendingResult?.items) ? pendingResult.items : [];
  const sourceItems =
    fromItems.length > 0
      ? fromItems
      : extractTransactionsList(pendingResult?.raw?.DATA || pendingResult?.raw);

  return sourceItems
    .map((item, index) => mapDashboardTransactionItem(item?.raw || item, index, "pending-live", t))
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, 4);
};

const mapStatisticsFromApi = (apiPayload, t) => {
  if (!apiPayload) return null;

  const dashboardRaw =
    apiPayload.DASHBOARD ||
    apiPayload.DATA?.DASHBOARD ||
    null;

  const statsRaw =
    dashboardRaw ||
    apiPayload.STATISTICS ||
    apiPayload.TRANSACTION_STATISTICS ||
    apiPayload.DASHBOARD_STATISTICS ||
    apiPayload.DATA?.STATISTICS ||
    apiPayload.DATA?.TRANSACTION_STATISTICS ||
    null;

  const recentRaw =
    dashboardRaw?.RECENT_TRANSACTIONS ||
    apiPayload.RECENT_TRANSACTIONS ||
    apiPayload.TRANSACTIONS ||
    apiPayload.DATA?.RECENT_TRANSACTIONS ||
    apiPayload.DATA?.TRANSACTIONS ||
    [];
  const pendingRaw =
    dashboardRaw?.PENDING_TRANSACTIONS ||
    apiPayload.PENDING_TRANSACTIONS ||
    apiPayload.DATA?.PENDING_TRANSACTIONS ||
    [];
  const recentList = extractTransactionsList(recentRaw);
  const pendingList = extractTransactionsList(pendingRaw);

  if (!statsRaw && recentList.length === 0 && pendingList.length === 0) {
    return null;
  }

  const mapped = {
    income: 0,
    expense: 0,
    transactionFlow: [],
    recentTransactions: [],
    pendingTransactions: [],
  };

  const chartRaw =
    dashboardRaw?.TRANSACTION_FLOW ||
    statsRaw?.TRANSACTION_FLOW ||
    statsRaw?.CHART ||
    statsRaw?.CHART_DATA ||
    statsRaw?.FLOW ||
    [];

  if (Array.isArray(chartRaw) && chartRaw.length > 0) {
    mapped.transactionFlow = chartRaw.slice(0, 8).map((item, index) => {
      const incomeValue = toNumberSafe(
        item?.INCOME ?? item?.income ?? item?.VALUE ?? item?.value,
        0
      );
      const expenseValue = toNumberSafe(
        item?.OUTCOME ?? item?.outcome ?? item?.EXPENSE ?? item?.expense,
        0
      );

      return {
      month:
        item?.MONTH ||
        item?.month ||
        item?.LABEL ||
        item?.label ||
        `M${index + 1}`,
      outcome: expenseValue,
      income: incomeValue,
    };
    });

    mapped.income = mapped.transactionFlow.reduce((sum, item) => sum + toNumberSafe(item.income), 0);
    mapped.expense = mapped.transactionFlow.reduce((sum, item) => sum + toNumberSafe(item.outcome), 0);
  } else {
    mapped.income = toNumberSafe(
      statsRaw?.INCOME ??
        statsRaw?.income ??
        statsRaw?.TOTAL_INCOME,
      0
    );
    mapped.expense = toNumberSafe(
      statsRaw?.EXPENSE ??
        statsRaw?.expense ??
        statsRaw?.TOTAL_EXPENSE,
      0
    );
  }

  if (recentList.length > 0) {
    mapped.recentTransactions = recentList.map((item, index) =>
      mapDashboardTransactionItem(item, index, "recent", t)
    );
  }

  if (pendingList.length > 0) {
    mapped.pendingTransactions = pendingList.map((item, index) =>
      mapDashboardTransactionItem(item, index, "pending", t)
    );
  } else if (mapped.recentTransactions.length > 0) {
    mapped.pendingTransactions = mapped.recentTransactions.filter((item) => item.isPending);
  }

  return hasDashboardStatistics(mapped) ? mapped : null;
};

/** Matches dashboard chart depth (`chartRaw.slice(0, 8)`). */
const SPENDING_FLOW_MONTH_COUNT = 8;

const REGISTRATION_DATE_KEYS = [
  "CREATED_AT",
  "created_at",
  "DATE_CREATED",
  "date_created",
  "REGISTER_DATE",
  "register_date",
  "JOINED_AT",
  "joined_at",
  "REGISTRATION_DATE",
  "registration_date",
  "ACCOUNT_CREATED_AT",
  "account_created_at",
  "CREATE_TIME",
  "create_time",
  "USER_CREATED_AT",
  "user_created_at",
  "JOIN_DATE",
  "join_date",
  "SIGNUP_DATE",
  "signup_date",
  "CLIENT_SINCE",
  "client_since",
  "MEMBER_SINCE",
  "member_since",
];

/** Normalize API date values (unix seconds, ISO strings, YYYY-MM-DD) for reliable month bucketing. */
const coerceToDate = (value) => {
  if (value == null || value === "") return null;

  if (typeof value === "number" && Number.isFinite(value)) {
    const ms = value > 0 && value < 1e12 ? value * 1000 : value;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
    if (ymd) {
      const y = Number(ymd[1]);
      const mo = Number(ymd[2]) - 1;
      const day = Number(ymd[3]);
      if (Number.isFinite(y) && Number.isFinite(mo) && Number.isFinite(day)) {
        const d = new Date(y, mo, day);
        if (!Number.isNaN(d.getTime())) return d;
      }
    }
  }

  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const parseRegistrationDate = (obj) => {
  if (!obj || typeof obj !== "object") return null;
  for (const key of REGISTRATION_DATE_KEYS) {
    const v = obj[key];
    if (v == null || v === "") continue;
    const d = coerceToDate(v);
    if (d) return d;
  }
  return null;
};

/** First day of the calendar month when the user account was created (if the API provides a date). */
const getUserRegistrationMonthStart = (user) => {
  if (!user) return null;
  let d = parseRegistrationDate(user);
  if (!d) {
    const info = user.user_information || user.USER_INFORMATION || user.userInformation || {};
    d = parseRegistrationDate(info);
  }
  if (!d) {
    const reg = user.user_regulation || user.USER_REGULATION || user.userRegulation || {};
    d = parseRegistrationDate(reg);
  }
  if (!d && Array.isArray(user.account)) {
    for (const acct of user.account) {
      if (!acct || typeof acct !== "object") continue;
      d = parseRegistrationDate(acct);
      if (d) break;
    }
  }
  if (!d) return null;
  return new Date(d.getFullYear(), d.getMonth(), 1);
};

/**
 * Up to `count` month buckets ending at the current month, but not before the user's registration month.
 */
const buildSpendingFlowMonthSlots = (count = SPENDING_FLOW_MONTH_COUNT, registrationMonthStart = null) => {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), 1);
  const idealStart = new Date(now.getFullYear(), now.getMonth() - (count - 1), 1);

  let start = idealStart;
  if (registrationMonthStart instanceof Date && !Number.isNaN(registrationMonthStart.getTime())) {
    const reg = new Date(
      registrationMonthStart.getFullYear(),
      registrationMonthStart.getMonth(),
      1
    );
    // Show at most `count` months ending at `end`, but do not start before the account existed.
    if (reg <= end) {
      start = reg.getTime() > idealStart.getTime() ? reg : idealStart;
    }
  }

  const slots = [];
  let y = start.getFullYear();
  let m = start.getMonth();
  const endY = end.getFullYear();
  const endM = end.getMonth();

  while (y < endY || (y === endY && m <= endM)) {
    const d = new Date(y, m, 1);
    slots.push({
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
      label: d.toLocaleDateString("en-GB", { month: "short" }),
      year: d.getFullYear(),
      monthIndex: d.getMonth(),
    });
    m += 1;
    if (m > 11) {
      m = 0;
      y += 1;
    }
  }

  return slots;
};

/**
 * Drops leading months where both income and expense are zero so new accounts don't show
 * a long empty tail before their first activity. Keeps at least one bucket when non-empty.
 */
const trimLeadingZeroFlowMonths = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return rows;
  let i = 0;
  while (
    i < rows.length - 1 &&
    toNumberSafe(rows[i]?.income, 0) === 0 &&
    toNumberSafe(rows[i]?.outcome, 0) === 0
  ) {
    i += 1;
  }
  return i === 0 ? rows : rows.slice(i);
};

/** Apply chart trim when stats come from API only (no client rebuild) or early-return paths. */
const withTrimmedTransactionFlow = (stats) => {
  if (!stats || !Array.isArray(stats.transactionFlow) || stats.transactionFlow.length === 0) {
    return stats;
  }
  const transactionFlow = trimLeadingZeroFlowMonths(stats.transactionFlow);
  if (transactionFlow.length === stats.transactionFlow.length) return stats;
  return {
    ...stats,
    transactionFlow,
  };
};

const getRawTransactionDate = (raw) => {
  const dateStr =
    raw?.TRANSACTION_DATE ||
    raw?.TRX_DATE ||
    raw?.CREATED_AT ||
    raw?.DATE ||
    raw?.date;
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Outcome = completed cash-out only (rejected / pending / failed do not count).
 * Income = completed wallet & MT5 cash-in only.
 */
const aggregateCompletedCashSpendingForSlots = (rawItems, monthSlots) => {
  const buckets = new Map();
  monthSlots.forEach((s) => buckets.set(s.key, { income: 0, outcome: 0 }));

  for (const raw of rawItems) {
    const statusRaw = raw?.STATUS ?? raw?.status ?? raw?.TRANSACTION_STATUS ?? raw?.transactionStatus;
    if (
      typeof statusRaw === "number" &&
      Number.isFinite(statusRaw) &&
      statusRaw !== TransactionStatus.COMPLETED
    ) {
      continue;
    }

    const typeCode = Number(raw?.TYPE ?? raw?.type);
    if (!Number.isFinite(typeCode)) continue;

    const amount = Math.abs(toNumberSafe(raw?.AMOUNT ?? raw?.amount ?? raw?.VALUE ?? 0, 0));
    const d = getRawTransactionDate(raw);
    if (!d) continue;

    const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const b = buckets.get(monthKey);
    if (!b) continue;
    if (typeCode === TransactionType.CASHOUT) {
      b.outcome += amount;
    } else if (typeCode === TransactionType.CASHIN || typeCode === TransactionType.CASHIN_MT5) {
      b.income += amount;
    }
  }

  const transactionFlow = monthSlots.map((s) => {
    const b = buckets.get(s.key);
    return { month: s.label, income: b.income, outcome: b.outcome };
  });
  const income = transactionFlow.reduce((sum, row) => sum + toNumberSafe(row.income, 0), 0);
  const expense = transactionFlow.reduce((sum, row) => sum + toNumberSafe(row.outcome, 0), 0);

  return { income, expense, transactionFlow };
};

const fetchAllCompletedTransactionsInRange = async (dateFrom, dateTo) => {
  const perPage = 100;
  const maxPages = 100;
  const collected = [];
  let page = 0;
  let lastPageIndex = 0;

  do {
    const result = await TransactionHistoryController.getAllTransactions({
      page,
      perPage,
      type: TransactionType.ALL,
      status: TransactionStatus.COMPLETED,
      dateFrom,
      dateTo,
      search: null,
      currency: null,
      walletId: null,
    });

    const items = result?.items || [];
    items.forEach((it) => {
      if (it?.raw) collected.push(it.raw);
    });

    lastPageIndex = Number(result?.pagination?.lastPage ?? 0);
    page += 1;
  } while (page <= lastPageIndex && page < maxPages);

  return collected;
};

const enrichDashboardStatsWithCompletedCashSpending = async (baseStats, dashboardUser = null) => {
  if (!baseStats) return baseStats;

  const registrationMonthStart = getUserRegistrationMonthStart(dashboardUser);
  const monthSlots = buildSpendingFlowMonthSlots(SPENDING_FLOW_MONTH_COUNT, registrationMonthStart);
  if (monthSlots.length === 0) return withTrimmedTransactionFlow(baseStats);

  const rangeStart = new Date(monthSlots[0].year, monthSlots[0].monthIndex, 1);
  const lastSlot = monthSlots[monthSlots.length - 1];
  const rangeEnd = new Date(lastSlot.year, lastSlot.monthIndex + 1, 0, 23, 59, 59, 999);

  const dateFrom = toApiDateParam(rangeStart, false);
  const dateTo = toApiDateParam(rangeEnd, true);
  if (!dateFrom || !dateTo) return withTrimmedTransactionFlow(baseStats);

  const rawList = await fetchAllCompletedTransactionsInRange(dateFrom, dateTo);
  const { income, expense, transactionFlow } = aggregateCompletedCashSpendingForSlots(
    rawList,
    monthSlots
  );

  return {
    ...baseStats,
    income,
    expense,
    transactionFlow: trimLeadingZeroFlowMonths(transactionFlow),
  };
};

/** Home-page load failures must end the session even during the resume grace window. */
const logoutAfterHomePageLoadFailure = () => {
  window.setTimeout(() => {
    forceSessionLogout();
  }, 400);
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("mobile");
  const [user, setUser] = useState(null);
  const [wallets, setWallets] = useState([]);
  const [orderedWallets, setOrderedWallets] = useState([]);
  const [activeWalletId, setActiveWalletId] = useState(null);
  const [notificationCount, setNotificationCount] = useState(0);
  const [pendingAgreement, setPendingAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [pendingTransactionsLive, setPendingTransactionsLive] = useState([]);
  const [activeRecentTab, setActiveRecentTab] = useState("history");
  const [refreshTick, setRefreshTick] = useState(0);
  const [agreementSigning, setAgreementSigning] = useState(false);
  const [forcedAgreementDetailsOpen, setForcedAgreementDetailsOpen] = useState(false);
  const [forcedAgreementSignatureSaved, setForcedAgreementSignatureSaved] = useState(false);
  const forcedAgreementSignatureCanvasRef = useRef(null);
  const { error: showError, success: showSuccess, warning: showWarning } = useToast();

  useEffect(() => {
    const handleDashboardRefresh = () => {
      setRefreshTick((prev) => prev + 1);
    };
    window.addEventListener("dashboard:refresh", handleDashboardRefresh);
    window.addEventListener("mfg:funds-refresh", handleDashboardRefresh);
    return () => {
      window.removeEventListener("dashboard:refresh", handleDashboardRefresh);
      window.removeEventListener("mfg:funds-refresh", handleDashboardRefresh);
    };
  }, []);

  useEffect(() => {
    const fetchHomePageInfos = async () => {
      try {
        setLoading(true);
        const shouldForceRefresh = refreshTick > 0;

        const [mainInfoResult, dashboardLoadResult, pendingTransactionsResult] = await Promise.allSettled([
          getHomePageInfos({ forceRefresh: shouldForceRefresh }),
          getHomePageInfos({ body: { LOAD_DASHBOARD: true }, forceRefresh: shouldForceRefresh }),
          PendingTransactionsController.getPendingTransactions({
            page: 0,
            perPage: 4,
            status: 1,
          }),
        ]);

        let mappedStatsFromDashboardLoad = null;
        if (dashboardLoadResult.status === "fulfilled") {
          const dashboardLoadData =
            dashboardLoadResult.value?.decryptedData || dashboardLoadResult.value?.data;
          mappedStatsFromDashboardLoad = mapStatisticsFromApi(dashboardLoadData, t);
        }

        if (pendingTransactionsResult.status === "fulfilled") {
          const mappedPending = mapPendingResultToDashboardItems(pendingTransactionsResult.value, t);
          setPendingTransactionsLive(mappedPending);
        } else {
          setPendingTransactionsLive([]);
        }

        if (mainInfoResult.status !== "fulfilled") {
          throw mainInfoResult.reason;
        }

        const response = mainInfoResult.value;
        const data = response?.decryptedData || response?.data;
        // Check if response is successful (matches Flutter: responce[AppConstants.CODE] != AppConstants.RESPONSE_OK)
        if (data && data.CODE === AppConstants.responseOk) {
          // Extract user data from DATA (matches Flutter: user = UserLogin.fromJson(responce['DATA']))
          if (data.DATA) {
            setUser(data.DATA);
            // Store user data in sessionStorage for other pages to use (avoids redundant API calls)
            try {
              sessionStorage.setItem('userData', JSON.stringify(data.DATA));
              sessionStorage.setItem('userDataTimestamp', Date.now().toString());
            } catch (storageError) {
            }
            
            // Save USER_ID from dashboard decrypted response for later use (e.g., financial / crypto info APIs)
            const userIdFromDashboard =
              data.DATA.USER_ID ||
              data.DATA.user_id ||
              data.DATA.ID ||
              data.DATA.id;
            if (userIdFromDashboard) {
              const userIdStr = userIdFromDashboard.toString();
              localStorage.setItem("userId", userIdStr);
              sessionStorage.setItem("userId", userIdStr);
            }
            
            if (data.DATA.account && Array.isArray(data.DATA.account) && data.DATA.account.length > 0) {
              const mergedWallets = WalletController.collectWalletsFromAccounts(data.DATA.account);
              if (mergedWallets.length > 0) {
                setWallets(mergedWallets);
                setOrderedWallets(mergedWallets);
                setActiveWalletId(mergedWallets[0].ID || mergedWallets[0].id);
              } else {
                setWallets([]);
                setOrderedWallets([]);
                setActiveWalletId(null);
              }
            } else {
              setWallets([]);
              setOrderedWallets([]);
              setActiveWalletId(null);
            }
          }
          
          // Extract notification counts (matches Flutter: responce['NOTIFICATIONS'])
          if (data.NOTIFICATIONS) {
            const n = data.NOTIFICATIONS.NOTIFICATIONS ?? 0;
            setNotificationCount(n);
            try {
              window.dispatchEvent(
                new CustomEvent("mfg:notification-badge", {
                  detail: { count: Number(n) || 0 },
                })
              );
            } catch (_) {}
          }
          
          // Extract pending agreement (matches Flutter: responce['PENDING_AGREEMENT'])
          if (data.PENDING_AGREEMENT) {
            setPendingAgreement(data.PENDING_AGREEMENT);
          } else {
            setPendingAgreement(null);
          }

          const baseDashboardStats =
            mappedStatsFromDashboardLoad ?? mapStatisticsFromApi(data, t);
          // LOAD_DASHBOARD already returns TRANSACTION_FLOW from the server — skip the
          // client-side paginated transaction history fetch (can be dozens of API calls).
          if (mappedStatsFromDashboardLoad) {
            setDashboardStats(withTrimmedTransactionFlow(baseDashboardStats));
          } else {
            try {
              const enriched = await enrichDashboardStatsWithCompletedCashSpending(
                baseDashboardStats,
                data.DATA
              );
              setDashboardStats(enriched);
            } catch {
              setDashboardStats(withTrimmedTransactionFlow(baseDashboardStats));
            }
          }
        } else {
          // Response code is not OK or response has error structure
          let errorMessage = 'Failed to load home page information';
          
          if (data) {
            if (data.message) {
              errorMessage = data.message;
            } else if (data.MESSAGE) {
              errorMessage = data.MESSAGE;
            } else if (data.CODE) {
              errorMessage = `API Error (Code: ${data.CODE})`;
            }
          }
          
          showError('Error Occurred', errorMessage);
          logoutAfterHomePageLoadFailure();
        }
      } catch (err) {
        showError('Error Occurred', err.message || 'Failed to load home page information');
        logoutAfterHomePageLoadFailure();
      } finally {
        setLoading(false);
      }
    };

    fetchHomePageInfos();
  }, [showError, refreshTick]);

  const handleCurrencyChange = (index) => {
    if (index < orderedWallets.length) {
      const selectedWallet = orderedWallets[index];
      // Reorder: move selected wallet to first position
      const newOrder = [
        selectedWallet,
        ...orderedWallets.slice(0, index),
        ...orderedWallets.slice(index + 1)
      ];
      setOrderedWallets(newOrder);
      setActiveWalletId(selectedWallet.ID || selectedWallet.id);
    }
  };

  // Small cards for the currencies that are not currently selected (desktop).
  // We keep them unique by currency code to avoid duplicates when multiple wallets share the same currency.
  const otherCurrencyWalletsForCards = useMemo(() => {
    const seen = new Set();
    const result = [];

    orderedWallets.forEach((w, idx) => {
      if (idx === 0) return; // skip selected wallet
      const codeRaw = w?.currencies?.CURRENCY_CODE || w?.CURRENCY_CODE || "";
      const code = codeRaw.toString().toUpperCase().trim();
      if (!code || seen.has(code)) return;
      seen.add(code);
      result.push({ wallet: w, index: idx });
    });

    return result;
  }, [orderedWallets]);

  const profileCompleted = user?.PROFILE_STATUS === 1;
  const shouldShowStatusBanner = getStatusBannerVisibility(user);
  const showDashboardStatistics = !loading && !shouldShowStatusBanner && hasDashboardStatistics(dashboardStats);
  const transactionFlow = dashboardStats?.transactionFlow || [];
  const recentTransactions = dashboardStats?.recentTransactions || [];
  const historyTransactions = recentTransactions.some((item) => item?.isPending)
    ? recentTransactions.filter((item) => !item?.isPending)
    : recentTransactions;
  const pendingTransactions =
    pendingTransactionsLive.length > 0
      ? pendingTransactionsLive
      : dashboardStats?.pendingTransactions || [];
  const selectedRecentTransactions =
    activeRecentTab === "pending" ? pendingTransactions.slice(0, 4) : historyTransactions.slice(0, 4);
  const flowMaxValue = Math.max(
    ...transactionFlow.flatMap((item) => [item.income, item.outcome]),
    1
  );
  const yAxisTicks = buildYAxisTicks(flowMaxValue);

  const handleProfileComplete = () => {
    setRefreshTick((prev) => prev + 1);
  };

  const handleStatusBannerAction = (statusInfo) => {
    if (statusInfo?.buttonText === t("status_banner_change_pin", { ns: "mobile" })) {
      navigate("/pages/settings/change-pin");
      return;
    }
  };

  const handleSeeAllTransactions = () => {
    if (activeRecentTab === "pending") {
      navigate("/pages/transactions/pending");
      return;
    }
    navigate("/pages/transactions/history");
  };

  const pendingAgreementKey = pendingAgreement?.AGREEMENT_KEY || pendingAgreement?.AGREEMENT_NAME || "";

  const dataUrlToFile = (dataUrl, fileName = "agreement-signature.png") => {
    const parts = String(dataUrl || "").split(",");
    if (parts.length < 2) return null;
    const mimeMatch = parts[0].match(/:(.*?);/);
    const mime = mimeMatch?.[1] || "image/png";
    const binary = atob(parts[1]);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new File([bytes], fileName, { type: mime });
  };

  const clearForcedAgreementSignature = () => {
    forcedAgreementSignatureCanvasRef.current?.clear();
    setForcedAgreementSignatureSaved(false);
  };

  const handleAgreementSignatureSelected = async () => {
    const signatureCanvas = forcedAgreementSignatureCanvasRef.current;
    if (!signatureCanvas || signatureCanvas.isEmpty()) {
      showError("Error Occurred", "Please sign inside the signature box first.");
      return;
    }
    const signatureDataUrl = signatureCanvas.toDataURL("image/png");
    const file = dataUrlToFile(signatureDataUrl, "forced-agreement-signature.png");
    if (!file || !pendingAgreement?.ID) return;

    try {
      setAgreementSigning(true);
      const response = await signForcedAgreement(pendingAgreement.ID, file);
      const result = response?.decryptedData || response?.data;
      if (Number(result?.CODE) === AppConstants.responseOk) {
        showSuccess("Success", "Agreement signed successfully.");
        setPendingAgreement(null);
        setRefreshTick((prev) => prev + 1);
      } else {
        showError("Error Occurred", result?.MESSAGE || `API Error (Code: ${result?.CODE || "Unknown"})`);
      }
    } catch (error) {
      showError("Error Occurred", error?.message || "Failed to sign agreement.");
    } finally {
      setAgreementSigning(false);
    }
  };

  useEffect(() => {
    if (!pendingAgreement?.ID) {
      setForcedAgreementDetailsOpen(false);
      clearForcedAgreementSignature();
    }
  }, [pendingAgreement?.ID]);

  if (!loading && user && !profileCompleted) {
    return (
      <div className="dashboard-page">
        <Sidebar user={user} />
        <ProfileCompletionWizard user={user} onComplete={handleProfileComplete} />
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <Sidebar user={user} />
      <div className="dashboard-content">
        {loading ? (
          <>
            <div className="dashboard-board">
              <div className="dashboard-wallet-row">
                <div className="balance-cards-main">
                  <CreditCard balance="0.0" currency="USD" user={null} isLoadingData={true} />
                </div>
                <div className="balance-cards-secondary-currencies">
                  <div className="balance-card-secondary-item">
                    <CreditCard balance="0.0" currency="EUR" isSmall={true} user={null} isLoadingData={true} />
                  </div>
                  <div className="balance-card-secondary-item">
                    <CreditCard balance="0.0" currency="GBP" isSmall={true} user={null} isLoadingData={true} />
                  </div>
                </div>
              </div>
              <div className="dashboard-board-accounts">
                <DashboardAccounts user={null} compact awaitReady />
              </div>
            </div>

            <div className="status-banner-mobile-wrapper">
              <StatusBanner user={null} isLoadingData={true} />
            </div>
          </>
        ) : (
          <>
            {/* Balance Cards Section - Render wallets from API */}
            {orderedWallets.length > 0 ? (
              <>
                {orderedWallets.length > 1 && (
                  <CurrencyToggleBar
                    wallets={orderedWallets}
                    activeWalletId={activeWalletId}
                    onCurrencyChange={handleCurrencyChange}
                  />
                )}

                <div className="dashboard-board">
                  <div className="dashboard-wallet-row">
                  {orderedWallets[0] && (
                    <div
                      key={`main-${orderedWallets[0].ID || orderedWallets[0].currencies?.CURRENCY_CODE}`}
                      className="balance-cards-main balance-card-transition"
                    >
                    <CreditCard 
                      balance={orderedWallets[0].CURRENT_BALENCE?.toString() || "0.0"} 
                      currency={orderedWallets[0].currencies?.CURRENCY_CODE || "USD"}
                      user={user}
                      onCashInNoPermission={() => {
                        if (showWarning) {
                          showWarning(
                            "Permission required",
                            "You don't have permission for Cash In right now."
                          );
                        }
                      }}
                      onCashOutNoPermission={() => {
                        if (showWarning) {
                          showWarning(
                            "Permission required",
                            "You don't have permission for Cash Out right now."
                          );
                        }
                      }}
                      onHistoryNoPermission={() => {
                        if (showWarning) {
                          showWarning(
                            "Permission required",
                            "You don't have permission for History right now."
                          );
                        }
                      }}
                    />
                  </div>
                )}

                  {otherCurrencyWalletsForCards.length > 0 && (
                    <div className="balance-cards-secondary-currencies">
                      {otherCurrencyWalletsForCards.map(({ wallet, index }) => (
                        <div
                          key={`secondary-${wallet.ID || wallet.id}-${wallet.currencies?.CURRENCY_CODE || wallet.CURRENCY_CODE}`}
                          className="balance-card-secondary-item"
                          role="button"
                          tabIndex={0}
                          onClick={() => handleCurrencyChange(index)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleCurrencyChange(index);
                            }
                          }}
                        >
                          <CreditCard
                            balance={wallet.CURRENT_BALENCE?.toString() || "0.0"}
                            currency={
                              wallet.currencies?.CURRENCY_CODE ||
                              wallet.CURRENCY_CODE ||
                              "USD"
                            }
                            isSmall={true}
                            user={null}
                            isLoadingData={false}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  </div>

                  <div className="dashboard-board-accounts">
                    <DashboardAccounts user={user} compact />
                  </div>

                  {!loading && (
                    <div className="status-banner-mobile-wrapper">
                      <StatusBanner
                        user={user}
                        onAction={handleStatusBannerAction}
                      />
                    </div>
                  )}

                  {showDashboardStatistics && (
                    <>
                <div className="dashboard-transaction-flow-card" data-page-tour="transaction_flow">
                  <div className="dashboard-transaction-flow-header">
                    <h3>
                      <i className="fa-solid fa-chart-column" />
                      {t("transaction_flow", { ns: "mobile", defaultValue: "Transaction Flow" })}
                    </h3>
                  </div>

                  <div className="dashboard-transaction-flow-metrics-row">
                    <div className="dashboard-transaction-flow-summary">
                      <div className="dashboard-transaction-flow-summary-item">
                        <span className="dashboard-transaction-flow-summary-label dashboard-transaction-flow-summary-label--income">
                          {t("deposit", { ns: "mobile", defaultValue: "Deposit" })}
                          <i className="fa-solid fa-arrow-up" />
                        </span>
                        <strong>{formatMoney(dashboardStats?.income)}</strong>
                      </div>
                      <div className="dashboard-transaction-flow-summary-item">
                        <span className="dashboard-transaction-flow-summary-label dashboard-transaction-flow-summary-label--expense">
                          {t("withdraw", { ns: "mobile", defaultValue: "Withdraw" })}
                          <i className="fa-solid fa-arrow-down" />
                        </span>
                        <strong>{formatMoney(dashboardStats?.expense)}</strong>
                      </div>
                    </div>

                    <div className="dashboard-transaction-flow-legend">
                      <div className="dashboard-transaction-flow-legend-item">
                        <span className="dashboard-transaction-flow-legend-color dashboard-transaction-flow-legend-color--income" />
                        <span>{t("deposit", { ns: "mobile", defaultValue: "Deposit" })}</span>
                      </div>
                      <div className="dashboard-transaction-flow-legend-item">
                        <span className="dashboard-transaction-flow-legend-color dashboard-transaction-flow-legend-color--expense" />
                        <span>{t("withdraw", { ns: "mobile", defaultValue: "Withdraw" })}</span>
                      </div>
                    </div>
                  </div>

                  <div className="dashboard-transaction-flow-chart-wrap">
                    <div className="dashboard-chart-y-axis">
                      {yAxisTicks.map((tick, index) => (
                        <span key={`${tick}-${index}`}>{formatTickValue(tick)}</span>
                      ))}
                    </div>

                    <div className="dashboard-chart-main">
                      <div className="dashboard-chart-grid-lines">
                        {[0, 1, 2, 3, 4, 5].map((idx) => (
                          <span key={idx} />
                        ))}
                      </div>

                      <div className="dashboard-chart-bars">
                        {transactionFlow.map((bar, index) => (
                          <div key={`${bar.month}-${index}`} className="dashboard-chart-month-group">
                            <div
                              className="dashboard-chart-bar dashboard-chart-bar--outcome"
                              style={{
                                height: `${Math.max((toNumberSafe(bar.outcome) / flowMaxValue) * 100, 2)}%`,
                              }}
                            />
                            <div
                              className="dashboard-chart-bar dashboard-chart-bar--income"
                              style={{
                                height: `${Math.max((toNumberSafe(bar.income) / flowMaxValue) * 100, 2)}%`,
                              }}
                            />
                          </div>
                        ))}
                      </div>

                      <div className="dashboard-chart-months">
                        {transactionFlow.map((bar, index) => (
                          <span key={`label-${bar.month}-${index}`}>{bar.month}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="dashboard-recent-transactions-card">
                  <div className="dashboard-recent-transactions-header">
                    <h3>
                      <i className="fa-solid fa-clock-rotate-left" />
                      {t("recent_transactions", { ns: "mobile", defaultValue: "Recent Transactions" })}
                    </h3>
                  </div>

                  <div className="dashboard-recent-transactions-tabs">
                    <button
                      type="button"
                      className={activeRecentTab === "history" ? "is-active" : ""}
                      onClick={() => setActiveRecentTab("history")}
                    >
                      {t("history", { ns: "mobile", defaultValue: "History" })}
                    </button>
                    <button
                      type="button"
                      className={activeRecentTab === "pending" ? "is-active" : ""}
                      onClick={() => setActiveRecentTab("pending")}
                    >
                      {t("pending", { ns: "mobile", defaultValue: "Pending" })}
                    </button>
                  </div>

                  <div className="dashboard-recent-transactions-list">
                    {selectedRecentTransactions.length > 0 ? (
                      selectedRecentTransactions.map((transaction) => (
                        <div key={transaction.id} className="dashboard-recent-transaction-item">
                          <div className="dashboard-recent-transaction-left">
                            <div
                              className={`dashboard-recent-transaction-avatar ${
                                transaction.typeClass || "type-real"
                              }`}
                            >
                              {String(transaction.avatar).slice(0, 2).toUpperCase()}
                            </div>
                            <div>
                              <strong>{transaction.title}</strong>
                            </div>
                          </div>

                          <div className="dashboard-recent-transaction-right">
                            <strong>{formatMoney(transaction.amount)}</strong>
                            <span>{transaction.date}</span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="dashboard-recent-transaction-item">
                        <div className="dashboard-recent-transaction-left">
                          <div>
                            <strong>
                              {activeRecentTab === "pending"
                                ? "No pending transactions"
                                : "No recent transactions"}
                            </strong>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <button
                    className="dashboard-recent-transactions-see-all"
                    type="button"
                    onClick={handleSeeAllTransactions}
                  >
                    See All
                    <i className="fa-solid fa-arrow-right" />
                  </button>
                </div>
                    </>
                  )}
                </div>
              </>
            ) : (
              <div className="error-message">
                No wallets found. Please contact support.
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Status Banner - Desktop absolute positioning */}
      {loading ? (
        <div className="status-banner-desktop-wrapper">
          <StatusBanner user={null} isLoadingData={true} />
        </div>
      ) : (
        <div className="status-banner-desktop-wrapper">
          <StatusBanner 
            user={user} 
            onAction={handleStatusBannerAction}
          />
        </div>
      )}

      {!!pendingAgreement?.ID && (
        <div className="forced-agreement-overlay" role="dialog" aria-modal="true">
          <div className="forced-agreement-modal">
            <div className="forced-agreement-modal__header">
              <button
                type="button"
                className="forced-agreement-modal__title-btn"
                onClick={() => setForcedAgreementDetailsOpen(true)}
                title="Click to view full agreement"
              >
                {pendingAgreement.AGREEMENT_NAME || "Agreement"}
              </button>
              <p>You must sign this agreement before using the dashboard.</p>
              <p className="forced-agreement-modal__hint">
                Tap the agreement title above to read the full document.
              </p>
            </div>
            <div className="forced-agreement-modal__body">
              <div className="forced-agreement-signature">
                <div className="forced-agreement-signature__label">Your Signature</div>
                <div className="forced-agreement-signature__canvas-wrap">
                  <SignatureCanvas
                    ref={forcedAgreementSignatureCanvasRef}
                    penColor="#141414"
                    canvasProps={{
                      className: "forced-agreement-signature__canvas",
                      width: 800,
                      height: 180,
                    }}
                    backgroundColor="#ffffff"
                    onEnd={() => setForcedAgreementSignatureSaved(true)}
                  />
                </div>
                <div className="forced-agreement-signature__actions">
                  <button
                    type="button"
                    className="forced-agreement-signature__clear"
                    onClick={clearForcedAgreementSignature}
                    disabled={agreementSigning}
                  >
                    Clear
                  </button>
                  {forcedAgreementSignatureSaved && (
                    <span className="forced-agreement-signature__saved">
                      Signature captured
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="forced-agreement-modal__footer">
              <button
                type="button"
                className="forced-agreement-modal__submit"
                onClick={handleAgreementSignatureSelected}
                disabled={agreementSigning}
              >
                {agreementSigning ? "Signing..." : "Sign & Continue"}
              </button>
            </div>
          </div>
        </div>
      )}

      <AgreementDetailsModal
        isOpen={forcedAgreementDetailsOpen}
        onClose={() => setForcedAgreementDetailsOpen(false)}
        agreementKey={pendingAgreementKey}
        agreementName={pendingAgreement?.AGREEMENT_NAME || "Agreement"}
        userId={user?.USER_ID || user?.ID || user?.id || null}
        closeLabel="Close"
      />

    </div>
  );
};

export default Dashboard;
